import { createFileRoute } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { useCallback, useMemo } from 'react';
import { z } from 'zod';

import type { NormalizedDateFilter } from '@/components/DatePicker/RichDatePicker';
import { useDateRangeFilter } from '@/hooks/useDateRangeFilter';
import { usePaginationSearch } from '@/lib/listPage';

import { EstimateDealTable } from './-components/EstimateDealTable';
import { EstimateSearchFilterBar } from './-components/EstimateSearchFilterBar';
import { useEstimateDealList } from './-query';

const FINALIZED_ONLY = ['Finalized'] as const;
const ALL_STATUSES = ['Finalized', 'Expired', 'Rejected'] as const;

const SearchSchema = z.object({
  pageIndex: z.number().default(0),
  pageSize: z.number().default(100),
  freeText: z.string().optional(),
  showRejectedExpired: z.boolean().default(true),
  validityFrom: z.string().optional(),
  validityTo: z.string().optional(),
});

export const Route = createFileRoute(
  '/_staff/device-catalog/active/$id/_layout/contract-and-cost/_contract-and-cost/deal/_deal/estimate/',
)({
  component: EstimateTab,
  validateSearch: zodValidator(SearchSchema),
});

function EstimateTab() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();

  const [pagination, setPagination] = usePaginationSearch({
    search,
    onSearchChange: (s) => navigate({ search: s }),
  });

  const statuses = useMemo(
    () =>
      search.showRejectedExpired ? [...ALL_STATUSES] : [...FINALIZED_ONLY],
    [search.showRejectedExpired],
  );

  const { data, isPending } = useEstimateDealList({
    page: search.pageIndex + 1,
    pageSize: search.pageSize,
    freeText: search.freeText,
    statuses,
  });

  const {
    currentFilter: validityDateFilter,
    handleFilterChange: handleValidityQueryChange,
  } = useDateRangeFilter(search.validityFrom, search.validityTo);

  const handleValidityDateFilterChange = useCallback(
    ({ normalized }: { normalized?: NormalizedDateFilter }) => {
      const { fromDate, toDate } = handleValidityQueryChange(normalized);
      navigate({
        search: (prev) => ({
          ...prev,
          validityFrom: fromDate,
          validityTo: toDate,
          pageIndex: 0,
        }),
      });
    },
    [navigate, handleValidityQueryChange],
  );

  const handleShowRejectedExpiredChange = useCallback(
    (checked: boolean) => {
      navigate({
        search: (prev) => ({
          ...prev,
          showRejectedExpired: checked,
          pageIndex: 0,
        }),
      });
    },
    [navigate],
  );

  return (
    <>
      <EstimateSearchFilterBar
        searchValue={search.freeText ?? ''}
        onSearchChange={(e) =>
          navigate({
            search: (prev) => ({
              ...prev,
              freeText: e.target.value || undefined,
              pageIndex: 0,
            }),
          })
        }
        validityDateFilter={validityDateFilter}
        onValidityDateFilterChange={handleValidityDateFilterChange}
        showRejectedExpired={search.showRejectedExpired}
        onShowRejectedExpiredChange={handleShowRejectedExpiredChange}
        onExport={() => {}}
      />
      <EstimateDealTable
        data={data?.items ?? []}
        isPending={isPending}
        totalCount={data?.count}
        pageCount={data?.maxPage}
        pagination={pagination}
        onPaginationChange={setPagination}
        className="flex-1 min-h-0"
      />
    </>
  );
}
